import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * ProtectedBusinessRoute - Component to protect business routes from unauthorized subaccount access
 * 
 * Role-based access:
 * - Admin: Full access to everything
 * - Manager: Access to stores, offers, transactions, feedback, insights
 * - Cashier: Limited access (transactions, feedback, qr operations only)
 * 
 * Usage:
 * <Route path="edit-profile" element={
 *   <ProtectedBusinessRoute ownerOnly>
 *     <BusinessEditProfile />
 *   </ProtectedBusinessRoute>
 * } />
 * 
 * OR with specific roles:
 * <Route path="offers" element={
 *   <ProtectedBusinessRoute allowedRoles={['Admin', 'Manager']}>
 *     <OffersManagement />
 *   </ProtectedBusinessRoute>
 * } />
 */
const ProtectedBusinessRoute = ({ children, ownerOnly = false, allowedRoles = [] }) => {
  const { authState } = useAuth();
  const location = useLocation();

  // Check if user is a subaccount
  const isSubaccount = authState?.store_id && authState?.role;
  const userRole = authState?.role;

  // Admin subaccounts have full access to everything
  if (isSubaccount && userRole === 'Admin') {
    return children;
  }

  // If this route is owner-only and user is a subaccount (non-Admin), redirect
  if (ownerOnly && isSubaccount) {
    console.warn('Subaccount attempted to access owner-only route:', location.pathname);
    return <Navigate to="/business" replace />;
  }

  // If specific roles are required and user doesn't have the role, redirect
  if (allowedRoles.length > 0 && isSubaccount && !allowedRoles.includes(userRole)) {
    console.warn('Subaccount with insufficient permissions attempted to access route:', location.pathname);
    return <Navigate to="/business" replace />;
  }

  // User has proper permissions, render the component
  return children;
};

export default ProtectedBusinessRoute;
